/**
 * Bottles page component
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Pagination, ConfirmDialog } from '../components/common';
import { BottleList, BottleForm, BottleFilters, BottleImageModal } from '../components/bottles';
import { HuntForm } from '../components/hunts';
import {
  useBottles,
  useCreateBottle,
  useUpdateBottle,
  useDeleteBottle
} from '../hooks/useBottles';
import { useCreateHunt } from '../hooks/useHunts';
import { useToast } from '../context/ToastContext';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../utils/constants';
import './Bottles.css';

/**
 * Bottles management page with CRUD operations
 * @returns {React.ReactElement} Page element
 */
export default function Bottles() {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE;
  const sortBy = searchParams.get('sort') || 'purchase_date';
  const sortOrder = searchParams.get('order') || 'desc';
  const filters = useMemo(() => {
    const f = {};
    const search = searchParams.get('search');
    const distillery = searchParams.get('distillery');
    const type = searchParams.get('type');
    const statusParam = searchParams.get('status');
    const size = searchParams.get('size');
    const storePick = searchParams.get('storePick');
    const country = searchParams.get('country');
    const state = searchParams.get('state');
    const priceMin = searchParams.get('priceMin');
    const priceMax = searchParams.get('priceMax');
    const priceLabel = searchParams.get('priceLabel');
    if (search) f.search = search;
    if (distillery) f.distillery_id = distillery;
    if (type) f.type = type;
    if (statusParam) f.status = statusParam.split(',').filter(Boolean);
    if (size) f.size = size;
    if (storePick) f.store_pick = storePick;
    if (country) f.country = country;
    if (state) f.state = state;
    if (priceMin) f.price_min = Number(priceMin);
    if (priceMax) f.price_max = Number(priceMax);
    if (priceLabel) f.price_label = priceLabel;
    return f;
  }, [searchParams]);

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedBottle, setSelectedBottle] = useState(null);
  const [bottleToDelete, setBottleToDelete] = useState(null);
  const [bottleToView, setBottleToView] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [bottleForHunt, setBottleForHunt] = useState(null);
  const [showHuntConfirm, setShowHuntConfirm] = useState(false);
  const [showHuntModal, setShowHuntModal] = useState(false);

  const hasActiveFilters = Object.keys(filters).some(k => k !== 'search');

  // Filters that arrive via a Dashboard chart click (no dedicated Select in
  // BottleFilters), shown as removable chips so they stay visible and clearable.
  const chartFilterChips = useMemo(() => {
    const chips = [];
    if (filters.state) chips.push({ key: 'state', label: `State: ${filters.state}` });
    if (filters.country) chips.push({ key: 'country', label: `Country: ${filters.country}` });
    if (filters.price_min != null || filters.price_max != null) {
      chips.push({ key: 'price', label: `Price: ${filters.price_label || 'Filtered'}` });
    }
    return chips;
  }, [filters]);

  const handleClearChartFilter = (key) => {
    if (key === 'price') {
      updateParams({ priceMin: '', priceMax: '', priceLabel: '', page: '' });
    } else {
      updateParams({ [key]: '', page: '' });
    }
  };

  const { data, isLoading } = useBottles({
    page,
    limit: pageSize,
    sortBy,
    sortOrder,
    filters
  });

  const createBottle = useCreateBottle();
  const updateBottle = useUpdateBottle();
  const deleteBottle = useDeleteBottle();
  const createHunt = useCreateHunt();

  const handleSort = (column, direction) => {
    updateParams({
      sort: column === 'purchase_date' ? '' : column,
      order: direction === 'desc' ? '' : direction,
    });
  };

  const handleFilterChange = (newFilters) => {
    updateParams({
      search: newFilters.search || '',
      distillery: newFilters.distillery_id || '',
      type: newFilters.type || '',
      status: Array.isArray(newFilters.status) && newFilters.status.length > 0
        ? newFilters.status.join(',')
        : '',
      size: newFilters.size || '',
      storePick: newFilters.store_pick || '',
      page: '',
    });
  };

  const handlePageSizeChange = (size) => {
    updateParams({
      pageSize: size === DEFAULT_PAGE_SIZE ? '' : size,
      page: '',
    });
  };

  const handlePageChange = (newPage) => {
    updateParams({ page: newPage === 1 ? '' : newPage });
  };

  const handleAddClick = () => {
    setSelectedBottle(null);
    setShowFormModal(true);
  };

  const handleEditClick = (bottle) => {
    setSelectedBottle(bottle);
    setShowFormModal(true);
  };

  const handleDeleteClick = (bottle) => {
    setBottleToDelete(bottle);
  };

  const handleViewClick = (bottle) => {
    navigate(`/bottles/${bottle.id}`);
  };

  const handleViewImages = (bottle) => {
    setBottleToView(bottle);
  };

  const handleFormSubmit = async (formData) => {
    try {
      const wasAlreadyEmpty = selectedBottle?.status === 'Empty';
      if (selectedBottle) {
        await updateBottle.mutateAsync({
          id: selectedBottle.id,
          updates: formData
        });
        addToast({ type: 'success', message: 'Bottle updated successfully' });
      } else {
        await createBottle.mutateAsync(formData);
        addToast({ type: 'success', message: 'Bottle added successfully' });
      }
      setShowFormModal(false);
      setSelectedBottle(null);
      if (formData.status === 'Empty' && !wasAlreadyEmpty) {
        setBottleForHunt({
          bottle_name: formData.bottle_name,
          type: formData.type || null,
          distillery_id: formData.distillery_id || null,
        });
        setShowHuntConfirm(true);
      }
    } catch (error) {
      addToast({
        type: 'error',
        message: error.message || 'Failed to save bottle'
      });
    }
  };

  const handleAddToHuntConfirm = () => {
    setShowHuntConfirm(false);
    setShowHuntModal(true);
  };

  const handleHuntFormSubmit = async (formData) => {
    try {
      await createHunt.mutateAsync(formData);
      addToast({ type: 'success', message: `"${formData.bottle_name}" added to your hunt list!` });
      setShowHuntModal(false);
      setBottleForHunt(null);
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Failed to add to hunt list' });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBottle.mutateAsync(bottleToDelete.id);
      addToast({ type: 'success', message: 'Bottle deleted successfully' });
      setBottleToDelete(null);
    } catch (error) {
      addToast({
        type: 'error',
        message: error.message || 'Failed to delete bottle'
      });
    }
  };

  const isFormLoading = createBottle.isPending || updateBottle.isPending;

  return (
    <div className="bottles-page">
      <div className="bottles-page__header">
        <h1 className="bottles-page__title">Bottles</h1>
        <div className="bottles-page__actions">
          <button
            className={`icon-btn icon-btn--filter${filtersOpen ? ' icon-btn--active' : ''}`}
            onClick={() => setFiltersOpen(prev => !prev)}
            aria-label="Toggle filters"
            title="Toggle filters"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {hasActiveFilters && !filtersOpen && <span className="icon-btn__badge" />}
          </button>
          <button
            className="icon-btn icon-btn--add"
            onClick={handleAddClick}
            aria-label="Add Bottle"
            title="Add Bottle"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      <BottleFilters filters={filters} onFilterChange={handleFilterChange} showAdvanced={filtersOpen} />

      {chartFilterChips.length > 0 && (
        <div className="bottles-page__chart-filters">
          {chartFilterChips.map(chip => (
            <span key={chip.key} className="bottles-page__filter-chip">
              {chip.label}
              <button
                type="button"
                className="bottles-page__filter-chip-remove"
                onClick={() => handleClearChartFilter(chip.key)}
                aria-label={`Remove filter ${chip.label}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <BottleList
        bottles={data?.data || []}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onSelect={handleViewClick}
        onViewImages={handleViewImages}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        loading={isLoading}
      />

      <Pagination
        currentPage={page}
        totalItems={data?.count || 0}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />

      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={selectedBottle ? 'Edit Bottle' : 'Add Bottle'}
        size="lg"
      >
        <BottleForm
          bottle={selectedBottle}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowFormModal(false)}
          loading={isFormLoading}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!bottleToDelete}
        onClose={() => setBottleToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Bottle"
        message={`Are you sure you want to delete "${bottleToDelete?.bottle_name}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteBottle.isPending}
      />

      <BottleImageModal
        isOpen={!!bottleToView}
        onClose={() => setBottleToView(null)}
        bottle={bottleToView}
      />

      <ConfirmDialog
        isOpen={showHuntConfirm}
        onClose={() => { setShowHuntConfirm(false); setBottleForHunt(null); }}
        onConfirm={handleAddToHuntConfirm}
        title="Add to Hunt List?"
        message={`"${bottleForHunt?.bottle_name}" is now empty. Would you like to add it to your hunt list so you can find another?`}
        confirmText="Yes, Add to Hunt List"
        cancelText="No Thanks"
        variant="primary"
      />

      <Modal
        isOpen={showHuntModal}
        onClose={() => { setShowHuntModal(false); setBottleForHunt(null); }}
        title="Add to Hunt List"
        size="md"
      >
        <HuntForm
          hunt={{
            bottle_name: bottleForHunt?.bottle_name || '',
            type: bottleForHunt?.type || '',
            distillery_id: bottleForHunt?.distillery_id || '',
            priority: '5',
          }}
          onSubmit={handleHuntFormSubmit}
          onCancel={() => { setShowHuntModal(false); setBottleForHunt(null); }}
          loading={createHunt.isPending}
        />
      </Modal>
    </div>
  );
}
